import type { RuntimeWidgetConfig } from "../../core/config/public-config.types.ts";
import {
  featureCategoryLabels,
  featureCategoryOrder,
  featurePanelRegistry,
  type FeaturePanelDefinition,
} from "./feature-panel-registry.ts";

type RuntimeFeatureMap = RuntimeWidgetConfig["features"];

const featureDescriptions: Record<string, string> = {
  "blue-filter": "Ekran tonunu yumuşatır",
  "contrast-mode": "Metin ayrımını artırır",
  "focus-emphasis": "Klavye odağını güçlendirir",
  "font-family": "Okunabilir font seçer",
  grayscale: "Renkleri sadeleştirir",
  "hide-images": "Görsel yoğunluğu azaltır",
  "highlight-headings": "Sayfa yapısını belirginleştirir",
  "highlight-links": "Linkleri görünür yapar",
  "large-cursor": "İmleci görünür yapar",
  "letter-spacing": "Harfleri ayırır",
  "line-spacing": "Satırlar arasında boşluk açar",
  "pause-animations": "Hareketi azaltır",
  "reading-guide": "Satır takibini kolaylaştırır",
  "reading-mode": "Satır genişliğini rahatlatır",
  "text-size": "Metni büyütür",
};

const featureIconSvgs: Record<string, string> = {
  "blue-filter": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <path d="M16 4c4.8 5.2 8 9.4 8 14a8 8 0 0 1-16 0c0-4.6 3.2-8.8 8-14Z"/>
    </svg>
  `,
  "contrast-mode": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <circle cx="16" cy="16" r="10"/>
      <path d="M16 6a10 10 0 0 1 0 20Z" fill="currentColor" stroke="none"/>
    </svg>
  `,
  "focus-emphasis": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <path d="M11 5H8a3 3 0 0 0-3 3v3M21 5h3a3 3 0 0 1 3 3v3M27 21v3a3 3 0 0 1-3 3h-3M11 27H8a3 3 0 0 1-3-3v-3"/>
      <circle cx="16" cy="16" r="3"/>
    </svg>
  `,
  "font-family": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <text x="6" y="22" font-family="Arial, Helvetica, sans-serif" font-size="17" font-weight="800" fill="currentColor" stroke="none">Tt</text>
    </svg>
  `,
  grayscale: `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <circle cx="16" cy="16" r="9"/>
      <path d="M12 8v16M16 7v18M20 8v16"/>
    </svg>
  `,
  "hide-images": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <path d="M6 8h20v16H6zM6 24l7-7 4 4 3-3 6 6M8 6l18 20"/>
    </svg>
  `,
  "highlight-headings": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <path d="M8 7v18M24 7v18M8 16h16"/>
    </svg>
  `,
  "highlight-links": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <path d="M13 10h-2a6 6 0 0 0 0 12h4M19 10h2a6 6 0 0 1 0 12h-4M12 16h8"/>
    </svg>
  `,
  "large-cursor": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <path d="M7 5l18 11-8 2 5 7-4 2-5-7-6 6Z"/>
    </svg>
  `,
  "letter-spacing": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <text x="4" y="22" font-family="Arial, Helvetica, sans-serif" font-size="17" font-weight="800" fill="currentColor" stroke="none">Aa</text>
    </svg>
  `,
  "line-spacing": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <path d="M8 9h16M8 16h16M8 23h16"/>
    </svg>
  `,
  "pause-animations": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <circle cx="16" cy="16" r="10"/>
      <path d="M13 12v8M19 12v8"/>
    </svg>
  `,
  "reading-guide": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <path d="M8 10l14 14 4-4L12 6Z"/>
      <path d="M13 9l-2 2M17 13l-2 2M21 17l-2 2"/>
    </svg>
  `,
  "reading-mode": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <path d="M6 8h8a4 4 0 0 1 4 4v12a4 4 0 0 0-4-4H6ZM26 8h-8a4 4 0 0 0-4 4v12a4 4 0 0 1 4-4h8Z"/>
    </svg>
  `,
  "text-size": `
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <path d="M8 8h16M16 8v18M11 26h10"/>
    </svg>
  `,
};

export type RenderFeatureControlInput = {
  config: unknown;
  definition: FeaturePanelDefinition;
  document: Document;
  key: string;
  onPreferenceChange: (nextValue: unknown) => void;
  preference: unknown;
};

export type CreateFeaturePanelContentInput = {
  availableFeatureKeys?: Set<string>;
  document?: Document;
  features: RuntimeFeatureMap;
  onPreferenceChange?: (key: string, nextValue: unknown) => void;
  preferences?: Record<string, unknown>;
  renderControl?: (input: RenderFeatureControlInput) => HTMLElement;
};

function isEnabledFeature(
  feature: RuntimeFeatureMap[string] | { enabled?: unknown; config?: unknown },
): feature is RuntimeFeatureMap[string] {
  return feature.enabled === true && "config" in feature;
}

function createTextElement(
  document: Document,
  tagName: keyof HTMLElementTagNameMap,
  className: string,
  text: string,
) {
  const element = document.createElement(tagName);
  element.className = className;
  element.textContent = text;
  return element;
}

function createDefaultControl(document: Document) {
  const element = document.createElement("p");
  element.className = "migrosone-a11y-widget-feature__placeholder";
  element.textContent = "Kontrol Epic 5 kapsamında etkinleştirilecek.";
  return element;
}

function createUnavailableControl(document: Document) {
  const element = document.createElement("p");
  element.className = "migrosone-a11y-widget-feature__unavailable";
  element.textContent = "Bu kontrol şu anda gösterilemiyor.";
  element.setAttribute("role", "status");
  return element;
}

function createFeatureIcon(document: Document, definition: FeaturePanelDefinition) {
  const icon = document.createElement("span");
  icon.className = "migrosone-a11y-widget-feature__icon";
  icon.setAttribute("aria-hidden", "true");
  icon.setAttribute("data-feature-key", definition.key);
  icon.innerHTML = featureIconSvgs[definition.key] ?? "";
  return icon;
}

function createFeatureItem({
  definition,
  document,
  feature,
  onPreferenceChange,
  preference,
  renderControl,
}: {
  definition: FeaturePanelDefinition;
  document: Document;
  feature: RuntimeFeatureMap[string];
  onPreferenceChange: (key: string, nextValue: unknown) => void;
  preference: unknown;
  renderControl: (input: RenderFeatureControlInput) => HTMLElement;
}) {
  const item = document.createElement("div");
  const copy = document.createElement("div");
  const title = createTextElement(
    document,
    "h4",
    "migrosone-a11y-widget-feature__title",
    definition.label,
  );
  const description = createTextElement(
    document,
    "p",
    "migrosone-a11y-widget-feature__description",
    featureDescriptions[definition.key] ?? "",
  );
  const controlSlot = document.createElement("div");

  item.className = "migrosone-a11y-widget-feature";
  item.setAttribute("data-feature-key", definition.key);
  copy.className = "migrosone-a11y-widget-feature__copy";
  controlSlot.className = "migrosone-a11y-widget-feature__control";
  copy.append(title, description);
  item.append(createFeatureIcon(document, definition), copy, controlSlot);

  try {
    controlSlot.append(
      renderControl({
        config: feature.config,
        definition,
        document,
        key: definition.key,
        onPreferenceChange(nextValue) {
          onPreferenceChange(definition.key, nextValue);
        },
        preference,
      }),
    );
  } catch {
    controlSlot.append(createUnavailableControl(document));
  }

  return item;
}

function createCategorySection({
  category,
  document,
  entries,
  onPreferenceChange,
  preferences,
  renderControl,
}: {
  category: (typeof featureCategoryOrder)[number];
  document: Document;
  entries: Array<{
    definition: FeaturePanelDefinition;
    feature: RuntimeFeatureMap[string];
  }>;
  onPreferenceChange: (key: string, nextValue: unknown) => void;
  preferences: Record<string, unknown>;
  renderControl: (input: RenderFeatureControlInput) => HTMLElement;
}) {
  const section = document.createElement("section");
  const heading = createTextElement(
    document,
    "h3",
    "migrosone-a11y-widget-features__heading",
    featureCategoryLabels[category],
  );
  const card = document.createElement("div");

  section.className = "migrosone-a11y-widget-features__category";
  section.setAttribute("data-category", category);
  card.className = "migrosone-a11y-widget-features__card";

  for (const { definition, feature } of entries) {
    card.append(
      createFeatureItem({
        definition,
        document,
        feature,
        onPreferenceChange,
        preference: preferences[definition.key],
        renderControl,
      }),
    );
  }

  section.append(heading, card);
  return section;
}

export function createFeaturePanelContent({
  availableFeatureKeys,
  document = globalThis.document,
  features,
  onPreferenceChange = () => {},
  preferences = {},
  renderControl = () => createDefaultControl(document),
}: CreateFeaturePanelContentInput): HTMLElement {
  const root = document.createElement("div");
  root.className = "migrosone-a11y-widget-features";

  const enabledKnownFeatures = featurePanelRegistry
    .map((definition) => {
      const feature = features[definition.key];
      if (availableFeatureKeys && !availableFeatureKeys.has(definition.key)) {
        return null;
      }

      return feature && isEnabledFeature(feature)
        ? { definition, feature }
        : null;
    })
    .filter(
      (entry): entry is {
        definition: FeaturePanelDefinition;
        feature: RuntimeFeatureMap[string];
      } => entry !== null,
    );

  if (enabledKnownFeatures.length === 0) {
    const emptyState = createTextElement(
      document,
      "p",
      "migrosone-a11y-widget-features__empty",
      "Bu site için etkin erişilebilirlik kontrolü yok.",
    );
    emptyState.setAttribute("role", "status");
    root.append(emptyState);
    return root;
  }

  for (const category of featureCategoryOrder) {
    const entries = enabledKnownFeatures.filter(({ definition }) => {
      return definition.category === category;
    });

    if (entries.length > 0) {
      root.append(
        createCategorySection({
          category,
          document,
          entries,
          onPreferenceChange,
          preferences,
          renderControl,
        }),
      );
    }
  }

  return root;
}
